import type { CSSProperties, ReactNode } from "react";

export type StateBadgeKind =
  | "active" | "draft" | "paused" | "concluded" | "retired"
  | "live" | "clean" | "warn" | "error" | "info";

interface KindSpec {
  color: string;
  pulse?: boolean;
}

const KIND_SPECS: Record<StateBadgeKind, KindSpec> = {
  active:    { color: "var(--success)" },
  draft:     { color: "var(--warning)" },
  paused:    { color: "var(--text-muted)" },
  concluded: { color: "var(--text-muted)" },
  retired:   { color: "var(--text-muted)" },
  live:      { color: "var(--accent)", pulse: true },
  clean:     { color: "var(--success)" },
  warn:      { color: "var(--warning)" },
  error:     { color: "var(--error)" },
  info:      { color: "var(--info)" },
};

/**
 * State indicator: subtle dot + sentence-case label.
 * Boardroom-tuned: no mono caps, no letter-spacing, soft tone.
 */
export function StateBadge({
  kind,
  children,
  style,
}: {
  kind: StateBadgeKind;
  children: ReactNode;
  style?: CSSProperties;
}) {
  const spec = KIND_SPECS[kind];
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        fontFamily: "var(--font-sans)",
        fontSize: 12.5,
        fontWeight: 500,
        color: spec.color,
        whiteSpace: "nowrap",
        ...style,
      }}
    >
      <span
        className={spec.pulse ? "live-pulse" : undefined}
        style={{
          width: 7,
          height: 7,
          borderRadius: "50%",
          background: spec.color,
          flexShrink: 0,
        }}
      />
      {typeof children === "string" ? toSentence(children) : children}
    </span>
  );
}

/* Soften shouty ALL-CAPS labels from older pages — "ACTIVE" → "Active" */
function toSentence(s: string): string {
  if (s.length === 0) return s;
  // Preserve mixed-case strings (e.g. JSX-rendered labels with formatting).
  // Only soften if string is fully upper-case or all-caps with separators.
  const isShouty = /^[A-Z0-9 _·\-/]+$/.test(s) && s.length > 1;
  if (!isShouty) return s;
  return s
    .toLowerCase()
    .split(/(\s+|[_·\-/])/)
    .map((part, i) => (i === 0 && part ? part[0].toUpperCase() + part.slice(1) : part))
    .join("");
}
